import type { SupportedLocale } from "../../i18n";
import type { Session } from "@supabase/supabase-js";

import type {
  PartnerApiRateLimitTier,
  PartnerApiScope
} from "@tinkr/shared/partner-controls";
import type { PartnerReferralFeeType } from "@tinkr/shared/payments";
import { idempotencyKey, tinkrApiFetch, tinkrApiPublicFetch } from "./client";

// ---------------------------------------------------------------------------
// Partner application submission flow (user-side)
// ---------------------------------------------------------------------------

export type PartnerApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn";

export type ExpectedMonthlyVolume =
  | "<100"
  | "100-1000"
  | "1k-10k"
  | ">10k";

export type PartnerApplication = {
  id: string;
  submittedByUserId: string | null;
  companyName: string;
  contactEmail: string;
  contactName: string | null;
  intendedUseCase: string;
  expectedMonthlyVolume: ExpectedMonthlyVolume;
  integrationPlan: string;
  proposedTier: PartnerApiRateLimitTier | null;
  status: PartnerApplicationStatus;
  reviewedAt: string | null;
  reviewedByUserId: string | null;
  decisionReason: string | null;
  partnerId: string | null;
  approvedRateLimitTier: PartnerApiRateLimitTier | null;
  createdAt: string;
  updatedAt: string;
};

export type SubmitPartnerApplicationInput = {
  preferredLocale?: SupportedLocale;
  companyName: string;
  contactEmail: string;
  contactName?: string;
  intendedUseCase: string;
  integrationPlan: string;
  expectedMonthlyVolume: ExpectedMonthlyVolume;
  proposedTier?: PartnerApiRateLimitTier;
  turnstileToken?: string;
};

export type SubmitPartnerApplicationResponse = {
  ok: true;
  application: PartnerApplication;
};

export type ListPartnerApplicationsSelfResponse = {
  ok: true;
  applications: PartnerApplication[];
};

export function submitPartnerApplication(
  session: Session | null,
  input: SubmitPartnerApplicationInput
): Promise<SubmitPartnerApplicationResponse> {
  const init = {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey("partner-application-submit") },
    body: JSON.stringify(input)
  };
  return session
    ? tinkrApiFetch<SubmitPartnerApplicationResponse>(
        session,
        "/v1/partner-applications",
        init
      )
    : tinkrApiPublicFetch<SubmitPartnerApplicationResponse>(
        "/v1/partner-applications",
        init
      );
}

export function listOwnPartnerApplications(
  session: Session
): Promise<ListPartnerApplicationsSelfResponse> {
  return tinkrApiFetch<ListPartnerApplicationsSelfResponse>(
    session,
    "/v1/partner-applications/me",
    { method: "GET" }
  );
}

export function withdrawOwnPartnerApplication(
  session: Session,
  applicationId: string
): Promise<SubmitPartnerApplicationResponse> {
  return tinkrApiFetch<SubmitPartnerApplicationResponse>(
    session,
    `/v1/partner-applications/${applicationId}/withdraw`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("partner-application-withdraw") },
      body: JSON.stringify({})
    }
  );
}

// ---------------------------------------------------------------------------
// Partner self-serve API key lifecycle
// ---------------------------------------------------------------------------

export type ApiKeyEnvironment = "sandbox" | "production";

export type PartnerApiKey = {
  id: string;
  name: string;
  scopes: string[];
  rateLimitTier: PartnerApiRateLimitTier;
  environment: ApiKeyEnvironment;
  partnerId: string;
  createdAt: string;
  createdBy: string;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokedReason: string | null;
  lastUsedAt: string | null;
  graceExpiresAt: string | null;
  prefix: string | null;
};

export type ListPartnerApiKeysSelfResponse = {
  ok: true;
  keys: PartnerApiKey[];
};

export type PartnerApiBriefSelfResponse = {
  ok: true;
  brief: string;
  sdk?: PartnerSdkMetadata;
};

export type PartnerSdkMetadata = {
  version: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  installCommand: string;
};

export type PartnerSdkDownloadResponse = PartnerSdkMetadata & {
  ok: true;
  contentType: "application/gzip";
  archiveBase64: string;
};

/**
 * Partner self-serve create-key input. `rateLimitTier` is intentionally
 * NOT here — the API rejects payloads that include it (PR #600 round 2),
 * because self-serve callers must not be able to elevate their own rate
 * limit. The tier inherits from `partners.rate_limit_tier`, set by an
 * admin at application approval. If future code needs to override, use
 * the admin key-mint endpoint instead.
 */
export type CreatePartnerApiKeySelfInput = {
  name: string;
  scopes: PartnerApiScope[];
  /** Which environment the key targets. Defaults to sandbox server-side;
   * production keys require production approval. */
  environment?: ApiKeyEnvironment;
  expiresAt?: string;
  reason: string;
};

export type CreatePartnerApiKeySelfResponse = {
  ok: true;
  key: PartnerApiKey;
  apiKey: string;
};

export type RotatePartnerApiKeySelfInput = {
  reason: string;
  gracePeriodSeconds?: number;
};

export type RotatePartnerApiKeySelfResponse = {
  ok: true;
  key: PartnerApiKey;
  oldKeyId: string;
  graceExpiresAt: string;
  apiKey: string;
};

export type RevokePartnerApiKeySelfInput = {
  reason: string;
};

export type RevokePartnerApiKeySelfResponse = {
  ok: true;
  keyId: string;
  revoked: true;
};

export function listPartnerApiKeysSelf(
  session: Session
): Promise<ListPartnerApiKeysSelfResponse> {
  return tinkrApiFetch<ListPartnerApiKeysSelfResponse>(
    session,
    "/v1/partner/api-keys",
    { method: "GET" }
  );
}

export function getPartnerApiBriefSelf(
  session: Session
): Promise<PartnerApiBriefSelfResponse> {
  return tinkrApiFetch<PartnerApiBriefSelfResponse>(
    session,
    "/v1/partner/api-brief",
    { method: "GET" }
  );
}

export function downloadPartnerSdkSelf(session: Session): Promise<PartnerSdkDownloadResponse> {
  return tinkrApiFetch<PartnerSdkDownloadResponse>(session, "/v1/partner/sdk/download", { method: "GET" });
}

export function createPartnerApiKeySelf(
  session: Session,
  input: CreatePartnerApiKeySelfInput
): Promise<CreatePartnerApiKeySelfResponse> {
  return tinkrApiFetch<CreatePartnerApiKeySelfResponse>(
    session,
    "/v1/partner/api-keys",
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("partner-api-key-self-create") },
      body: JSON.stringify(input)
    }
  );
}

export function rotatePartnerApiKeySelf(
  session: Session,
  keyId: string,
  input: RotatePartnerApiKeySelfInput
): Promise<RotatePartnerApiKeySelfResponse> {
  return tinkrApiFetch<RotatePartnerApiKeySelfResponse>(
    session,
    `/v1/partner/api-keys/${keyId}/rotate`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("partner-api-key-self-rotate") },
      body: JSON.stringify(input)
    }
  );
}

export function revokePartnerApiKeySelf(
  session: Session,
  keyId: string,
  input: RevokePartnerApiKeySelfInput
): Promise<RevokePartnerApiKeySelfResponse> {
  return tinkrApiFetch<RevokePartnerApiKeySelfResponse>(
    session,
    `/v1/partner/api-keys/${keyId}/revoke`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("partner-api-key-self-revoke") },
      body: JSON.stringify(input)
    }
  );
}

// ---------------------------------------------------------------------------
// Partner lifecycle overview + production launch applications (self-serve)
// ---------------------------------------------------------------------------

export type PartnerAccessState =
  | "sandbox_active"
  | "production_review_pending"
  | "production_active"
  | "suspended"
  | "terminated";

export type PartnerProductionApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "changes_requested";

export type PartnerProductionApplication = {
  id: string;
  partnerId: string;
  status: PartnerProductionApplicationStatus;
  appName: string;
  appUrl: string | null;
  useCase: string;
  targetCountryCodes: string[];
  expectedMonthlyVolume: string;
  supportContactEmail: string;
  usersUploadFiles: boolean;
  aiModelsUsed: boolean;
  paymentFlowSummary: string;
  termsUrl: string | null;
  privacyUrl: string | null;
  notes: string | null;
  reviewedAt: string | null;
  decisionReason: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PartnerSelfOverview = {
  ok: true;
  partner: {
    id: string;
    displayName: string;
    status: "pending" | "active" | "suspended" | "terminated";
    accessStage: ApiKeyEnvironment;
    state: PartnerAccessState;
  };
  productionApplication: PartnerProductionApplication | null;
  keyCounts: { sandbox: number; production: number };
};

export type SubmitPartnerProductionApplicationInput = {
  appName: string;
  appUrl?: string;
  useCase: string;
  targetCountryCodes?: string[];
  expectedMonthlyVolume: string;
  supportContactEmail: string;
  usersUploadFiles?: boolean;
  aiModelsUsed?: boolean;
  paymentFlowSummary: string;
  termsUrl?: string;
  privacyUrl?: string;
  notes?: string;
};

export type SubmitPartnerProductionApplicationResponse = {
  ok: true;
  application: PartnerProductionApplication;
};

export function getPartnerSelfOverview(session: Session): Promise<PartnerSelfOverview> {
  return tinkrApiFetch<PartnerSelfOverview>(session, "/v1/partner/overview");
}

export function submitPartnerProductionApplication(
  session: Session,
  input: SubmitPartnerProductionApplicationInput
): Promise<SubmitPartnerProductionApplicationResponse> {
  return tinkrApiFetch<SubmitPartnerProductionApplicationResponse>(
    session,
    "/v1/partner/production-applications",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("partner-production-application-submit")
      },
      body: JSON.stringify(input)
    }
  );
}

// ---------------------------------------------------------------------------
// Partner webhook subscription lifecycle (self-serve, Partner Platform Batch P3)
// ---------------------------------------------------------------------------

export type PartnerWebhookSubscription = {
  id: string;
  partnerId: string;
  endpointUrl: string;
  eventTypes: string[];
  enabled: boolean;
  deliveryMode: string;
  signingSecretName: string | null;
  maxAttempts: number;
  lastDeliveryAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListPartnerWebhookSubscriptionsSelfResponse = {
  ok: true;
  subscriptions: PartnerWebhookSubscription[];
};

// PR #603 round 2: self-serve callers no longer pass signingSecretName.
// The API generates a per-subscription signing secret server-side on
// create and returns it ONCE via signingSecret on the create response.
// Admin-managed subscriptions (admin.ts surface) still use named env bindings.
export type CreatePartnerWebhookSubscriptionSelfInput = {
  reason: string;
  endpointUrl: string;
  eventTypes: string[];
  enabled?: boolean;
  maxAttempts?: number;
};

export type CreatePartnerWebhookSubscriptionSelfResponse = {
  ok: true;
  subscriptionId: string;
  status: string;
  // Present on create only. Surface in the UI exactly once — there is no
  // second-read endpoint, mirror the API-key one-time reveal model.
  signingSecret?: string;
};

export type PartnerWebhookSubscriptionTransitionResponse = {
  ok: true;
  subscriptionId: string;
  status: string;
};

export type UpdatePartnerWebhookSubscriptionSelfInput = {
  reason: string;
  endpointUrl?: string;
  eventTypes?: string[];
  enabled?: boolean;
  maxAttempts?: number;
};

export type DeletePartnerWebhookSubscriptionSelfInput = {
  reason: string;
};

export type DeletePartnerWebhookSubscriptionSelfResponse = {
  ok: true;
  subscriptionId: string;
};

export function listPartnerWebhookSubscriptionsSelf(
  session: Session
): Promise<ListPartnerWebhookSubscriptionsSelfResponse> {
  return tinkrApiFetch<ListPartnerWebhookSubscriptionsSelfResponse>(
    session,
    "/v1/partner/webhook-subscriptions"
  );
}

export function createPartnerWebhookSubscriptionSelf(
  session: Session,
  input: CreatePartnerWebhookSubscriptionSelfInput
): Promise<CreatePartnerWebhookSubscriptionSelfResponse> {
  return tinkrApiFetch<CreatePartnerWebhookSubscriptionSelfResponse>(
    session,
    "/v1/partner/webhook-subscriptions",
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("partner-webhook-subscription-self-create")
      },
      body: JSON.stringify(input)
    }
  );
}

export function updatePartnerWebhookSubscriptionSelf(
  session: Session,
  subscriptionId: string,
  input: UpdatePartnerWebhookSubscriptionSelfInput
): Promise<PartnerWebhookSubscriptionTransitionResponse> {
  return tinkrApiFetch<PartnerWebhookSubscriptionTransitionResponse>(
    session,
    `/v1/partner/webhook-subscriptions/${subscriptionId}/update`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("partner-webhook-subscription-self-update")
      },
      body: JSON.stringify(input)
    }
  );
}

export function deletePartnerWebhookSubscriptionSelf(
  session: Session,
  subscriptionId: string,
  input: DeletePartnerWebhookSubscriptionSelfInput
): Promise<DeletePartnerWebhookSubscriptionSelfResponse> {
  return tinkrApiFetch<DeletePartnerWebhookSubscriptionSelfResponse>(
    session,
    `/v1/partner/webhook-subscriptions/${subscriptionId}/delete`,
    {
      method: "POST",
      headers: {
        "idempotency-key": idempotencyKey("partner-webhook-subscription-self-delete")
      },
      body: JSON.stringify(input)
    }
  );
}

// ---------------------------------------------------------------------------
// Sandbox order playground (session-authed dashboard testing)
// ---------------------------------------------------------------------------

export type SandboxOrderState =
  | "created"
  | "estimating"
  | "quote_locked"
  | "delivered"
  | "completed"
  | "cancelled";

export type SandboxOrderScenario = "fulfilled" | "no_eligible_provider";

export interface SandboxOrderProvider {
  providerId: string;
  name: string | null;
  currency: string | null;
  totalMinor: number | null;
  estimatedDays: number | null;
}

export interface SandboxOrder {
  id: string;
  environment: "sandbox";
  simulated: true;
  state: SandboxOrderState;
  scenario: SandboxOrderScenario;
  isTerminal: boolean;
  nextState: SandboxOrderState | null;
  request: { material: string; quantity: number; specialties: string[] };
  externalUserRef: string | null;
  externalConversationRef: string | null;
  selectedProvider: SandboxOrderProvider | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSandboxOrderInput {
  material: string;
  quantity: number;
  specialties?: string[];
}

export function listSandboxOrders(
  session: Session
): Promise<{ ok: true; jobs: SandboxOrder[] }> {
  return tinkrApiFetch<{ ok: true; jobs: SandboxOrder[] }>(
    session,
    "/v1/partner/sandbox-orders",
    { method: "GET" }
  );
}

export function createSandboxOrder(
  session: Session,
  input: CreateSandboxOrderInput
): Promise<{ ok: true; job: SandboxOrder }> {
  return tinkrApiFetch<{ ok: true; job: SandboxOrder }>(
    session,
    "/v1/partner/sandbox-orders",
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("sandbox-order-create") },
      body: JSON.stringify(input)
    }
  );
}

export function advanceSandboxOrder(
  session: Session,
  sandboxJobId: string
): Promise<{ ok: true; job: SandboxOrder }> {
  return tinkrApiFetch<{ ok: true; job: SandboxOrder }>(
    session,
    `/v1/partner/sandbox-orders/${sandboxJobId}/advance`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("sandbox-order-advance") },
      body: JSON.stringify({})
    }
  );
}

// ---------------------------------------------------------------------------
// Partner governance for new production launch review. This is independent of
// sandbox API access and of Wise recipient registration.
// ---------------------------------------------------------------------------

export type PartnerIdentityStatus =
  | "not_started" | "requires_input" | "processing" | "verified"
  | "failed" | "cancelled" | "expired";
export type PartnerBusinessReviewStatus =
  | "not_submitted" | "pending" | "approved" | "rejected"
  | "more_information_required";
export type PartnerGovernanceResponse = {
  ok: true;
  partnerId: string;
  identityVerification: {
    status: PartnerIdentityStatus;
    provider: string | null;
    verificationUrl: string | null;
    startedAt: string | null;
    updatedAt: string | null;
  };
  businessReview: {
    status: PartnerBusinessReviewStatus;
    submittedAt: string | null;
    updatedAt: string | null;
    partnerMessage: string | null;
  };
  blockingReasons: string[];
};

export function getPartnerGovernance(session: Session): Promise<PartnerGovernanceResponse> {
  return tinkrApiFetch(session, "/v1/partner/governance", { method: "GET" });
}

export function startPartnerIdentityVerification(
  session: Session,
  input: { returnUrl: string }
): Promise<{ ok: true; identityVerification: PartnerGovernanceResponse["identityVerification"] }> {
  return tinkrApiFetch(session, "/v1/partner/identity-verification", {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey("partner-identity-verification") },
    body: JSON.stringify(input)
  });
}

export function submitPartnerBusinessReview(
  session: Session,
  input: { registrationCountryCode: string; registrationNumber: string }
): Promise<{ ok: true; partnerId: string; status: "pending"; eventId: string }> {
  return tinkrApiFetch(session, "/v1/partner/business-review", {
    method: "POST",
    headers: { "idempotency-key": idempotencyKey("partner-business-review-submit") },
    body: JSON.stringify(input)
  });
}

// Partner Payouts / Earnings summary and self-serve Wise recipient setup.
//
// Founder decision: partner payouts are AUTOMATIC after the commission hold
// (ADR-P11 M5). There is NO request-payout action — this is a read-only page.
// The DTO is partner-safe: balances, paid history, payout setup status,
// and the payout minimum only. It never carries Tinkr economics
// decomposition, raw payment ids, or buyer PII (enforced server-side).
// ---------------------------------------------------------------------------

export const partnerPayoutConnectStatuses = [
  "not_started",
  "set_up",
  "ready",
  "action_needed"
] as const;

export type PartnerPayoutConnectStatus =
  (typeof partnerPayoutConnectStatuses)[number];

export type PartnerPayoutBalance = {
  currency: string;
  accruedMinor: number;
  heldMinor: number;
  eligibleMinor: number;
  paidMinor: number;
};

export type PartnerPayoutHistoryItem = {
  id: string;
  amountMinor: number;
  currency: string;
  status: "paid";
  paidAt: string | null;
  createdAt: string;
};

export type PartnerPayoutSummary = {
  ok: true;
  partnerId: string;
  connect: {
    status: PartnerPayoutConnectStatus;
    detailsSubmitted: boolean;
    payoutsEnabled: boolean;
    verificationStatus: "unverified" | "pending" | "verified" | "rejected";
    hasAccount: boolean;
  };
  payoutSetup: {
    status: "not_started" | "pending" | "configured" | "disabled";
    provider: "wise_batch_payout" | null;
    currencies: string[];
    identityVerified: boolean;
    businessApproved: boolean;
  };
  payoutMinimum: {
    amountMinor: number | null;
    currency: string | null;
  };
  balances: PartnerPayoutBalance[];
  history: PartnerPayoutHistoryItem[];
};

export type PartnerWisePayoutSetupResponse = {
  ok: true;
  mode: "demo" | "live";
  pending: boolean;
  payoutSetup: {
    status: PartnerPayoutSummary["payoutSetup"]["status"];
    provider: PartnerPayoutSummary["payoutSetup"]["provider"];
  };
  recipient: { currency: string; wiseRecipientAccountId: string } | null;
};

/** Registers an email recipient for one payout currency. The API resolves
 * the partner's account identity; bank details are entered with Wise later. */
export function configurePartnerWisePayoutSetup(
  session: Session,
  input: { currency: string }
): Promise<PartnerWisePayoutSetupResponse> {
  return tinkrApiFetch<PartnerWisePayoutSetupResponse>(
    session,
    "/v1/partner/payout-setup/wise-email",
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey("partner-wise-payout-setup") },
      body: JSON.stringify(input)
    }
  );
}

export function getPartnerPayoutSummary(
  session: Session
): Promise<PartnerPayoutSummary> {
  return tinkrApiFetch<PartnerPayoutSummary>(
    session,
    "/v1/partner/payout-summary",
    { method: "GET" }
  );
}

// ---------------------------------------------------------------------------
// Partner Orders & Analytics (WS4 — session/dashboard, READ-ONLY).
//
// The partner reads THEIR OWN orders (paginated) and simple roll-ups. The DTOs
// are partner-safe: a GROUPED public price per order (item subtotal + shipping
// + total) and a small status set — never Tinkr's economics decomposition, raw
// payment ids, shipping addresses, or buyer PII (enforced server-side).
// ---------------------------------------------------------------------------

export const partnerOrderStatuses = [
  "draft",
  "estimating",
  "failed",
  "quoted",
  "matching",
  "no_match",
  "awaiting_payment",
  "paid",
  "in_production",
  "shipped",
  "delivered",
  "completed",
  "cancelled"
] as const;

export type PartnerOrderStatus = (typeof partnerOrderStatuses)[number];

export type PartnerOrderPrice = {
  currency: string;
  itemSubtotalMinor: number;
  shippingMinor: number;
  totalMinor: number;
  /** ADR premium §4.3: the customer-visible "Premium fulfilment" line, excluded
   * from itemSubtotalMinor. Present only when the locked quote is premium. */
  premiumServiceMinor?: number;
};

/** ADR premium §4.2: premium order terms on a partner order. */
export type PartnerOrderPremium = {
  requested: boolean;
  required: boolean;
  upliftBps: number | null;
  unavailableReason: "no_premium_providers" | null;
};

export type PartnerOrderSummary = {
  orderId: string;
  orderReference: string;
  status: PartnerOrderStatus;
  createdAt: string;
  updatedAt: string;
  /** GROUPED public price — null until the order is priced (quote locked). */
  price: PartnerOrderPrice | null;
  /** Optional so rows from an older API degrade to standard terms. */
  premium?: PartnerOrderPremium;
};

export type ListPartnerOrdersResponse = {
  ok: true;
  partnerId: string;
  orders: PartnerOrderSummary[];
  page: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
};

export type PartnerAnalyticsResponse = {
  ok: true;
  partnerId: string;
  generatedAt: string;
  totals: {
    orderCount: number;
    paidOrderCount: number;
  };
  countsByStatus: Record<PartnerOrderStatus, number>;
  /** Grouped paid order volume per currency (usually one key in v1). */
  paidVolumeByCurrency: Record<string, number>;
};

export function listPartnerOrders(
  session: Session,
  params?: { limit?: number; offset?: number }
): Promise<ListPartnerOrdersResponse> {
  const search = new URLSearchParams();
  if (params?.limit !== undefined) search.set("limit", String(params.limit));
  if (params?.offset !== undefined) search.set("offset", String(params.offset));
  const query = search.toString();
  return tinkrApiFetch<ListPartnerOrdersResponse>(
    session,
    `/v1/partner/orders${query ? `?${query}` : ""}`,
    { method: "GET" }
  );
}

export function getPartnerAnalytics(
  session: Session
): Promise<PartnerAnalyticsResponse> {
  return tinkrApiFetch<PartnerAnalyticsResponse>(
    session,
    "/v1/partner/analytics",
    { method: "GET" }
  );
}

// ---------------------------------------------------------------------------
// Partner Shipping Coverage (WS-SHIP PR5 — session/dashboard, READ-ONLY).
//
// Aggregate eligibility summary: which countries Tinkr can fulfil to, by mode.
// The DTO is partner-safe by construction (enforced server-side): boolean
// domestic/international availability + a coarse status + curated bubble NAMES
// + the admin customer notice ONLY. It carries NO provider identity, NO
// per-provider/threshold counts, NO economics, and NO amounts (this is route
// eligibility, not pricing).
// ---------------------------------------------------------------------------

export type PartnerCoverageStatus = "available" | "coming_soon" | "blocked";

export type PartnerShippingCoverageBubble = {
  slug: string;
  name: string;
};

export type PartnerShippingCoverageCountry = {
  countryCode: string;
  status: PartnerCoverageStatus;
  domesticAvailable: boolean;
  internationalAvailable: boolean;
  bubbles: PartnerShippingCoverageBubble[];
  notice: string;
};

export type PartnerShippingCoverageResponse = {
  ok: true;
  generatedAt: string;
  countries: PartnerShippingCoverageCountry[];
  bubbles: PartnerShippingCoverageBubble[];
};

export function getPartnerShippingCoverage(
  session: Session
): Promise<PartnerShippingCoverageResponse> {
  return tinkrApiFetch<PartnerShippingCoverageResponse>(
    session,
    "/v1/partner/shipping-coverage",
    { method: "GET" }
  );
}

// ---------------------------------------------------------------------------
// Provider matching preference (ADR-P10 §5 — session/dashboard).
//
// A partner chooses which provider entity divisions may fulfill its jobs:
// both (the default), registered businesses only, or individual makers only.
// Same session posture as the balance reads above (partner owner via Supabase
// session, not a partner API key). The PUT sits under /v1/* so the global
// idempotency middleware requires an Idempotency-Key, exactly like the other
// self-serve writes in this module.
// ---------------------------------------------------------------------------

// "individual_only" was dropped on 2026-10-24: those orders use the normal
// ranked offer queue (ADR-M5).
export const providerEntityPreferences = ["both", "business_only"] as const;

export type ProviderEntityPreference = (typeof providerEntityPreferences)[number];

export type PartnerMatchingPreferencesResponse = {
  ok: true;
  providerEntityPreference: ProviderEntityPreference;
};

export function getPartnerMatchingPreferences(
  session: Session
): Promise<PartnerMatchingPreferencesResponse> {
  return tinkrApiFetch<PartnerMatchingPreferencesResponse>(
    session,
    "/v1/partner/matching-preferences",
    { method: "GET" }
  );
}

export function updatePartnerMatchingPreferences(
  session: Session,
  input: { providerEntityPreference: ProviderEntityPreference }
): Promise<PartnerMatchingPreferencesResponse> {
  return tinkrApiFetch<PartnerMatchingPreferencesResponse>(
    session,
    "/v1/partner/matching-preferences",
    {
      method: "PUT",
      headers: { "idempotency-key": idempotencyKey("partner-matching-preferences") },
      body: JSON.stringify(input)
    }
  );
}

// ---------------------------------------------------------------------------
// Partner payment notification preferences (session/dashboard).
//
// v1 exposes the `payment` category only: in-app alerts are always on, this
// surface toggles the email channel plus the locale payment emails are sent
// in. Same session posture as the matching preference above (partner owner
// via Supabase session). The PUT is an idempotent upsert and sits under /v1,
// so the global idempotency middleware requires an Idempotency-Key.
// ---------------------------------------------------------------------------

export type PartnerPaymentNotificationPreference = {
  category: "payment";
  inAppEnabled: boolean;
  emailEnabled: boolean;
  /** Normalized server-side to the supported locale set; falls back to "en". */
  preferredLocale: string;
};

export type PartnerNotificationPreferencesResponse = {
  ok: true;
  preference: PartnerPaymentNotificationPreference;
};

export type UpdatePartnerPaymentPreferenceInput = {
  emailEnabled: boolean;
  preferredLocale?: string;
};

export function getPartnerNotificationPreferences(
  session: Session
): Promise<PartnerNotificationPreferencesResponse> {
  return tinkrApiFetch<PartnerNotificationPreferencesResponse>(
    session,
    "/v1/partner/notification-preferences",
    { method: "GET" }
  );
}

export function updatePartnerPaymentNotificationPreference(
  session: Session,
  input: UpdatePartnerPaymentPreferenceInput
): Promise<PartnerNotificationPreferencesResponse> {
  return tinkrApiFetch<PartnerNotificationPreferencesResponse>(
    session,
    "/v1/partner/notification-preferences/payment",
    {
      method: "PUT",
      headers: {
        "idempotency-key": idempotencyKey("partner-notification-preferences-payment")
      },
      body: JSON.stringify(input)
    }
  );
}

// ---------------------------------------------------------------------------
// Partner referral fee summary (READ-ONLY agreement terms).
//
// The partner's OWN current effective referral fee rule, deliberately minimal
// server-side (no internal notes, no rule history): fee shape + hold period +
// payout minimum + agreement reference. Amount fields are integer minor units
// in their sibling currency field (fee_cap_amount / payout_minimum_amount are
// minor units per the P10 fee-rules migration). `currentRule: null` means no
// commission rule is configured for this partner yet.
// ---------------------------------------------------------------------------

export type { PartnerReferralFeeType };

export type PartnerReferralFeeRuleSummary = {
  feeType: PartnerReferralFeeType;
  /** Basis points for percent / capped_percent rules; 0 otherwise. */
  feeValueBps: number;
  /** Flat commission (minor units of feeCurrency) for fixed rules; 0 otherwise. */
  feeAmountMinor: number;
  feeCurrency: string | null;
  /** Cap (minor units of feeCapCurrency) for capped_percent rules. */
  feeCapAmount: number | null;
  feeCapCurrency: string | null;
  holdPeriodDays: number;
  payoutMinimumAmount: number | null;
  payoutMinimumCurrency: string | null;
  agreementReference: string | null;
  effectiveFrom: string;
};

export type PartnerReferralFeeSummaryResponse = {
  ok: true;
  partnerId: string;
  currentRule: PartnerReferralFeeRuleSummary | null;
};

export function getPartnerReferralFeeSummary(
  session: Session
): Promise<PartnerReferralFeeSummaryResponse> {
  return tinkrApiFetch<PartnerReferralFeeSummaryResponse>(
    session,
    "/v1/partner/referral-fee-summary",
    { method: "GET" }
  );
}
